package diana.soleil.design.iraniancanadienbazzar.core.model

import kotlinx.serialization.Serializable

/**
 * A directory category (`categories/{categoryId}`). [iconKey] is a Material Symbols name resolved by
 * the design system; [businessCount] is a Cloud-Functions-maintained aggregate. [nameLocalized]
 * carries the EN/FR/FA display names (seeded — same pattern as metros); [name] stays the canonical
 * English fallback for older documents.
 */
@Serializable
data class Category(
    val id: String,
    val name: String,
    val iconKey: String,
    val businessCount: Int = 0,
    val order: Int = 0,
    val nameLocalized: LocalizedText? = null,
    /** Approved listings per metro id (`gta`, `mtl`, …), maintained server-side. */
    val businessCountByMetro: Map<String, Int> = emptyMap(),
    /** Approved listings per country code (`CA`, `US`), maintained server-side. */
    val businessCountByCountry: Map<String, Int> = emptyMap(),
) {
    /**
     * The count that matches what tapping the tile will list: the active metro's, else the active
     * country's, else the global figure. A tile that says "13 businesses" while the list behind it
     * is empty (13 in the GTA, 0 in Montréal) is a lie the member cannot decode — this is the fix.
     * Empty maps (a category doc the backfill has not reached yet) fall through to the old global
     * number rather than to zero.
     */
    fun countFor(metroId: String?, countryCode: String?): Int = when {
        metroId != null && businessCountByMetro.isNotEmpty() -> businessCountByMetro[metroId] ?: 0
        countryCode != null && businessCountByCountry.isNotEmpty() -> businessCountByCountry[countryCode] ?: 0
        else -> businessCount
    }

    /** Display name for [language]: the localized variant if provided, else the canonical name. */
    fun displayName(language: Language): String =
        nameLocalized?.resolve(language)?.takeIf { it.isNotBlank() } ?: name
}
